class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.runningiOS = /(iPad|iPhone|iPod)/g.test(navigator.userAgent);
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.details = this.closest('details');
    this.detailsModal = this.details.closest('details-modal');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.siteHeader = document.querySelector('.shopify-section-group-header-group');
    this.searchHeader = document.querySelector('.search-header');
    this.setupEventListeners();
    this.swatchCarouselOptions = {
      slidesToShow: 6,
      slidesToScroll: 5,
      breakpoints: {
        992: {
          slidesToShow: 8,
          slidesToScroll: 7,
        },
      },
    };
    this.swatchCarousels = [];
    this.swatchResizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const carousel = this.swatchCarousels.find((c) => c.element === entry.target);
        if (carousel) {
          carousel.resize();
        }
      }
    });
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    document.querySelectorAll('[js-search-open]').forEach((element) => {
      element.addEventListener('click', this.onClickOpen.bind(this));
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    document.body.style.setProperty('overflow-y', 'hidden', 'important');
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if (!this.runningiOS) {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onClickOpen(event) {
    this.details.setAttribute('open', true);
    this.detailsModal.setAttribute('open', true);
    this.input.focus();
    this.input.selectionStart = this.input.value.length;
    document.body.classList.add('overflow-hidden');
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g'))?.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null,
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.swatchCarouselInit();
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      (this.searchHeader
        ? this.searchHeader.getBoundingClientRect().bottom
        : this.siteHeader.getBoundingClientRect().bottom);
    return this.resultsMaxHeight;
  }

  swatchCarouselInit() {
    this.details.querySelectorAll('.colour-swatch-links.js-colour-swatch-links').forEach((element) => {
      const swatches = element.querySelectorAll('.swatch');
      const size = swatches.length;

      if (size > 7) {
        // Create vanilla JS carousel instead of using jQuery slick
        const carousel = new VanillaSwatchCarousel(element, this.swatchCarouselOptions);
        this.swatchCarousels.push(carousel);
        this.swatchResizeObserver.observe(element);
      }
    });

    this.details.querySelectorAll('.additional-swatches-trigger').forEach((element) => {
      element.addEventListener('click', () => {
        const el = element.closest('.predictive-search-product');
        const url = el.querySelector('.product-card__link').getAttribute('href');
        window.location.href = url;
      });
    });
  }

  open() {
    this.predictiveSearchResults.style.setProperty(
      '--search-header-max-height',
      `${this.resultsMaxHeight || this.getResultsMaxHeight() - 76}px`,
    );

    this.predictiveSearchResults.style.setProperty('overflow-y', 'auto');

    if (window.innerWidth <= 991) {
      const searchModalContainer = document.querySelector('.search__modal-container').offsetHeight;
      this.predictiveSearchResults.style.height = `calc(100vh - ${searchModalContainer}px)`;
    }

    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    document.body.style.setProperty('overflow-y', 'auto', 'important');
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

// Custom vanilla JS carousel implementation to replace jQuery slick
class VanillaSwatchCarousel {
  constructor(element, options) {
    this.element = element;
    this.options = options;
    this.currentSlide = 0;
    this.slideWidth = 0;
    this.totalSlides = element.querySelectorAll('.swatch').length;
    this.slidesToShow = this.getSlidesToShow();
    this.slidesToScroll = this.getSlidesToScroll();

    this.init();
  }

  init() {
    // Create wrapper for slides
    this.slideTrack = this.element.querySelector(':scope > *') || this.element;
    this.slideTrack.style.display = 'flex';
    this.slideTrack.style.transition = 'transform 0.3s ease';
    this.slideTrack.style.width = '100%';
    this.slideTrack.style.overflow = 'hidden';

    // Create navigation buttons
    this.createNavButtons();

    // Set initial state
    this.resize();
    this.updateButtonState();
  }

  createNavButtons() {
    // Create previous button
    this.prevButton = document.createElement('div');
    this.prevButton.className = 'swatch-slider-prev';
    this.prevButton.innerHTML = '<button class="swatch-slider-prev-arrow" type="button"></button>';
    this.prevButton.addEventListener('click', () => this.goToPrev());

    // Create next button
    this.nextButton = document.createElement('div');
    this.nextButton.className = 'swatch-slider-next';
    this.nextButton.innerHTML = '<button class="swatch-slider-next-arrow" type="button"></button>';
    this.nextButton.addEventListener('click', () => this.goToNext());

    // Add buttons to DOM
    const parent = this.element.parentNode;
    parent.insertBefore(this.prevButton, this.element);
    parent.insertBefore(this.nextButton, this.element.nextSibling);
  }

  getSlidesToShow() {
    if (window.innerWidth >= 992 && this.options.breakpoints && this.options.breakpoints[992]) {
      return this.options.breakpoints[992].slidesToShow;
    }
    return this.options.slidesToShow;
  }

  getSlidesToScroll() {
    if (window.innerWidth >= 992 && this.options.breakpoints && this.options.breakpoints[992]) {
      return this.options.breakpoints[992].slidesToScroll;
    }
    return this.options.slidesToScroll;
  }

  resize() {
    this.slidesToShow = this.getSlidesToShow();
    this.slidesToScroll = this.getSlidesToScroll();

    // Calculate slide width
    const containerWidth = this.element.offsetWidth;
    this.slideWidth = containerWidth / this.slidesToShow;

    // Set width for each slide
    const slides = this.element.querySelectorAll('.swatch');
    slides.forEach((slide) => {
      slide.style.flex = `0 0 ${this.slideWidth}px`;
    });

    // Update position
    this.goToSlide(this.currentSlide);
  }

  goToSlide(index) {
    // Ensure index is within bounds
    this.currentSlide = Math.max(0, Math.min(index, this.totalSlides - this.slidesToShow));

    // Update transform
    const offset = -this.currentSlide * this.slideWidth;
    this.slideTrack.style.transform = `translateX(${offset}px)`;

    // Update button state
    this.updateButtonState();
  }

  goToNext() {
    this.goToSlide(this.currentSlide + this.slidesToScroll);
  }

  goToPrev() {
    this.goToSlide(this.currentSlide - this.slidesToScroll);
  }

  updateButtonState() {
    // Disable prev button if at start
    if (this.currentSlide <= 0) {
      this.prevButton.classList.add('disabled');
    } else {
      this.prevButton.classList.remove('disabled');
    }

    // Disable next button if at end
    if (this.currentSlide >= this.totalSlides - this.slidesToShow) {
      this.nextButton.classList.add('disabled');
    } else {
      this.nextButton.classList.remove('disabled');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
